import { api } from "./apiSlice";
import { unwrapApiResponse } from "../utils/apiUtils";
import { RootState } from "../app/store";

export interface SubscriptionPlan {
  id: number;
  code: string;
  name: string;
  description?: string | null;
  pricePerVehicle: number;
  currency: string;
  trialPeriodDays: number;
  isActive: boolean;
  displayOrder: number;
  maxVehicles?: number | null;
  maxUsers?: number | null;
  features?: string | null;
}

export interface SignupTenant {
  id: number;
  name: string;
  contactEmail?: string | null;
  contactPhone?: string | null;
  isActive: boolean;
  stripeCustomerId?: string | null;
  createdAt: string;
  updatedAt?: string | null;
}

export interface SignupSubscription {
  id: number;
  stripeSubscriptionId?: string | null;
  tenantId: number;
  status: string;
  startDate: string;
  trialEndDate?: string | null;
  currentPeriodEnd: string;
  endDate?: string | null;
  vehicleQuantity?: number;
}

export interface SignupPaymentMethod {
  id: string;
  type: string;
  cardBrand?: string | null;
  cardLast4?: string | null;
  cardExpiryMonth?: number | null;
  cardExpiryYear?: number | null;
  isDefault: boolean;
}

export interface SignupRequest {
  subscriptionPlanId: number;
  tenantName: string;
  contactEmail?: string;
  contactPhone?: string;
  vehicleCount: number;
  paymentMethodId: string;
}

export interface SignupResult {
  tenant: SignupTenant;
  subscription: SignupSubscription;
  paymentMethod: SignupPaymentMethod;
  planName: string;
  planCode: string;
  trialPeriodDays: number;
}

export interface Payment {
  id: number;
  amount: number;
  currency: string;
  status: string;
  description?: string | null;
  stripePaymentIntentId: string;
  createdAt: string;
  paidAt?: string | null;
  failureMessage?: string | null;
}

export interface BillingDetails {
  subscription?: SignupSubscription | null;
  subscriptionPlan?: SubscriptionPlan | null;
  paymentMethod?: SignupPaymentMethod | null;
  recentPayments: Payment[];
  cancelAtPeriodEnd: boolean;
  canReactivate: boolean;
}

export interface UpdatePaymentMethodRequest {
  paymentMethodId: string;
}

export interface ReactivateSubscriptionRequest {
  subscriptionPlanId: number;
  vehicleCount: number;
}

export interface CancelSubscriptionResponse {
  success: boolean;
  message: string;
  canceledImmediately: boolean;
}

export interface ResumeSubscriptionResponse {
  success: boolean;
  message: string;
}

export interface SubscriptionListItem {
  subscriptionId: number;
  tenantId: number;
  tenantName: string;
  status: string;
  startDate: string;
  vehicleQuantity: number;
  syncedVehicleCount: number;
  planName?: string | null;
}

/** Current tenant subscription level for feature gating (Lite, Standard, Pro). */
export interface SubscriptionLevelResponse {
  level: string | null;
}

export const subscriptionApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getSubscriptionPlans: builder.query<SubscriptionPlan[], void>({
      query: () => ({
        url: "SubscriptionPlan",
        params: { activeOnly: true },
      }),
      transformResponse: unwrapApiResponse<SubscriptionPlan[]>,
    }),
    signup: builder.mutation<SignupResult, SignupRequest>({
      query: (body) => ({
        url: "public/signup",
        method: "POST",
        body,
      }),
      transformResponse: unwrapApiResponse<SignupResult>,
    }),
    getBillingDetails: builder.query<BillingDetails, void>({
      query: () => ({
        url: "Subscription",
      }),
      transformResponse: unwrapApiResponse<BillingDetails>,
      providesTags: ["Subscription"],
      // Don't cache subscription data - it's critical and must be fresh
      keepUnusedDataFor: 0,
    }),
    getPaymentHistory: builder.query<Payment[], number | void>({
      query: (limit = 10) => ({
        url: "Subscription/payment-history",
        params: { limit },
      }),
      transformResponse: unwrapApiResponse<Payment[]>,
      providesTags: ["PaymentHistory"],
    }),
    updatePaymentMethod: builder.mutation<
      SignupPaymentMethod,
      UpdatePaymentMethodRequest
    >({
      query: (body) => ({
        url: "Subscription/payment-method",
        method: "PUT",
        body,
      }),
      transformResponse: unwrapApiResponse<SignupPaymentMethod>,
      invalidatesTags: ["Subscription"],
    }),
    cancelSubscription: builder.mutation<
      CancelSubscriptionResponse,
      boolean | void
    >({
      query: (immediately = false) => ({
        url: "Subscription",
        method: "DELETE",
        params: { immediately },
      }),
      transformResponse: unwrapApiResponse<CancelSubscriptionResponse>,
      invalidatesTags: ["Subscription"],
    }),
    cancelTenantSubscription: builder.mutation<
      CancelSubscriptionResponse,
      { tenantId: number; immediately?: boolean }
    >({
      query: ({ tenantId, immediately = true }) => ({
        url: `Subscription/tenant/${tenantId}`,
        method: "DELETE",
        params: { immediately },
      }),
      transformResponse: unwrapApiResponse<CancelSubscriptionResponse>,
      invalidatesTags: ["Subscription", "TenantsList"],
    }),
    resumeSubscription: builder.mutation<ResumeSubscriptionResponse, void>({
      query: () => ({
        url: "Subscription/resume",
        method: "POST",
      }),
      transformResponse: unwrapApiResponse<ResumeSubscriptionResponse>,
      invalidatesTags: ["Subscription"],
    }),
    reactivateSubscription: builder.mutation<
      SignupSubscription,
      ReactivateSubscriptionRequest
    >({
      query: (body) => ({
        url: "Subscription/reactivate",
        method: "POST",
        body,
      }),
      transformResponse: unwrapApiResponse<SignupSubscription>,
      invalidatesTags: ["Subscription"],
    }),
    getSubscriptionsList: builder.query<SubscriptionListItem[], void>({
      query: () => ({
        url: "Subscription/list",
      }),
      transformResponse: unwrapApiResponse<SubscriptionListItem[]>,
      providesTags: ["Subscription"],
    }),
    getSubscriptionLevel: builder.query<SubscriptionLevelResponse, void>({
      query: () => ({
        url: "Subscription/subscription-level",
      }),
      transformResponse: unwrapApiResponse<SubscriptionLevelResponse>,
      providesTags: ["Subscription"],
      keepUnusedDataFor: 0,
    }),
    checkTenantName: builder.query<boolean, string>({
      query: (name) => ({
        url: "public/signup/check-tenant-name",
        params: { name },
      }),
      transformResponse: unwrapApiResponse<boolean>,
    }),
    checkEmail: builder.query<boolean, string>({
      query: (email) => ({
        url: "public/signup/check-email",
        params: { email },
      }),
      transformResponse: unwrapApiResponse<boolean>,
    }),
  }),
});

export const {
  useGetSubscriptionPlansQuery,
  useSignupMutation,
  useGetBillingDetailsQuery,
  useGetPaymentHistoryQuery,
  useUpdatePaymentMethodMutation,
  useCancelSubscriptionMutation,
  useCancelTenantSubscriptionMutation,
  useResumeSubscriptionMutation,
  useReactivateSubscriptionMutation,
  useGetSubscriptionsListQuery,
  useGetSubscriptionLevelQuery,
  useLazyCheckTenantNameQuery,
  useLazyCheckEmailQuery,
} = subscriptionApi;

/** Selector: current subscription level from Redux store (updated on each load/focus). */
export const selectSubscriptionLevel = (state: RootState): string | null =>
  subscriptionApi.endpoints.getSubscriptionLevel.select(undefined)(state).data?.level ?? null;
